import { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useSWRConfig } from "swr";

import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPause,
  faPlay,
  faTimes,
  faExclamationTriangle,
  faCreditCard
} from "@fortawesome/free-solid-svg-icons";

import { AnimatedLogo } from "@aclymatepackages/atoms";
import { formatDollars, formatDecimal } from "@aclymatepackages/formatters";

import Card from "../../atoms/mui/Card";
import PaymentInput from "../../modules/PaymentInput";

import { useCachedDisplayData } from "../../../helpers/firebase";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../../helpers/analytics";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";

dayjs.extend(advancedFormat);

const FREQUENCY_DISPLAY_MAP = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly"
};

const STATUS_DISPLAY_MAP = {
  active: { label: "Active", color: "success" },
  paused: { label: "Paused", color: "default" },
  canceled: { label: "Canceled", color: "default" },
  payment_failed: { label: "Payment failed", color: "error" }
};

const formatDateOrFallback = (value, fallback = "—") => {
  if (!value) {
    return fallback;
  }
  return dayjs(value).format("MMM D, YYYY");
};

const UpdatePaymentMethodDialog = ({ subscriptionId, onClose }) => {
  const { paymentMethod } = useContext(StripeCustomerContext);
  const { mutate } = useSWRConfig();
  const [initialPaymentMethodId] = useState(paymentMethod?.paymentMethodId);
  const [savedStatus, setSavedStatus] = useState("idle");

  useEffect(() => {
    if (savedStatus !== "idle") {
      return;
    }
    const currentId = paymentMethod?.paymentMethodId;
    if (!currentId || currentId === initialPaymentMethodId) {
      return;
    }

    setSavedStatus("saving");
    const isAch = Boolean(paymentMethod?.us_bank_account);
    const accountId = window.sessionStorage.getItem("accountId");

    fetchOurApi({
      path: "/offset-subscriptions/update",
      method: "POST",
      data: {
        accountId,
        subscriptionId,
        paymentMethodId: currentId,
        paymentType: isAch ? "ach" : "card"
      },
      callback: (response) => {
        if (response?.error) {
          setSavedStatus("error");
          return;
        }
        analyticsTrack("Offset Subscription Payment Method Updated", {
          subscriptionId
        });
        mutate("offset-subscriptions");
        setSavedStatus("saved");
        onClose();
      }
    }).catch(() => setSavedStatus("error"));
  }, [
    paymentMethod,
    initialPaymentMethodId,
    subscriptionId,
    mutate,
    onClose,
    savedStatus
  ]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update payment method</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Enter your new card or bank account. We'll use it for all future
          cycles on this subscription. Your old payment method will be detached.
        </Typography>
        <PaymentInput buttonText="Save new payment method" />
        {savedStatus === "error" ? (
          <Typography variant="body2" color="error">
            Something went wrong saving the payment method. Please try again.
          </Typography>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const buildAnchorDescription = (firstChargeDate, frequency) => {
  if (!firstChargeDate) {
    return null;
  }
  const anchor = dayjs(firstChargeDate);
  if (frequency === "monthly") {
    return `Anchored to the ${anchor.format("Do")} of each month`;
  }
  if (frequency === "quarterly") {
    return `Anchored to the ${anchor.format("Do")} every 3 months`;
  }
  return `Anchored to ${anchor.format("MMMM Do")} each year`;
};

const SubscriptionCard = ({ subscription, offsets }) => {
  const {
    id,
    status,
    portfolioName,
    portfolioImage,
    budgetDollars,
    frequency,
    nextBillingDate,
    firstChargeDate,
    lastSuccessfulCycleAt,
    lastError
  } = subscription;

  const anchorDescription = buildAnchorDescription(firstChargeDate, frequency);

  const { mutate } = useSWRConfig();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);

  const statusInfo = STATUS_DISPLAY_MAP[status] || {
    label: status,
    color: "default"
  };
  const isActive = status === "active";
  const isPaused = status === "paused" || status === "payment_failed";
  const isCanceled = status === "canceled";

  const relatedOffsets = (offsets || []).filter(
    (offset) => offset.subscriptionId === id
  );

  const callSubscriptionAction = async ({ action, analyticsEvent }) => {
    setActionLoading(true);
    setActionError(null);
    const accountId = window.sessionStorage.getItem("accountId");
    return await fetchOurApi({
      path: `/offset-subscriptions/${action}`,
      method: "POST",
      data: { accountId, subscriptionId: id },
      callback: (response) => {
        if (response?.error) {
          setActionError(response.message || "Action failed.");
          setActionLoading(false);
          return;
        }
        analyticsTrack(analyticsEvent, { subscriptionId: id });
        mutate("offset-subscriptions");
        setActionLoading(false);
      }
    }).catch((e) => {
      setActionError(e?.message || "Action failed.");
      setActionLoading(false);
    });
  };

  const pauseSubscription = () =>
    callSubscriptionAction({
      action: "pause",
      analyticsEvent: "Offset Subscription Paused"
    });

  const resumeSubscription = () =>
    callSubscriptionAction({
      action: "resume",
      analyticsEvent: "Offset Subscription Resumed"
    });

  const cancelSubscription = () => {
    if (
      !window.confirm(
        "Cancel this offset subscription? You can start a new one anytime."
      )
    ) {
      return null;
    }
    return callSubscriptionAction({
      action: "cancel",
      analyticsEvent: "Offset Subscription Canceled"
    });
  };

  return (
    <>
      {isUpdatingPayment ? (
        <UpdatePaymentMethodDialog
          subscriptionId={id}
          onClose={() => setIsUpdatingPayment(false)}
        />
      ) : null}
      <Card
      gridProps={{ xs: 12 }}
      content={
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              {portfolioImage ? (
                <Box
                  style={{
                    backgroundImage: `url(${portfolioImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    paddingTop: "100%",
                    borderRadius: 4
                  }}
                />
              ) : null}
            </Grid>
            <Grid item xs={12} sm={7}>
              <Grid container direction="column" spacing={1}>
                <Grid item container spacing={1} alignItems="center">
                  <Grid item>
                    <Typography variant="h6" color="textPrimary">
                      {portfolioName}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Chip
                      size="small"
                      label={statusInfo.label}
                      color={statusInfo.color}
                    />
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    {FREQUENCY_DISPLAY_MAP[frequency] || frequency} ·{" "}
                    {formatDollars(budgetDollars)} per cycle
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    {isCanceled
                      ? `Canceled on ${formatDateOrFallback(subscription.canceledAt)}`
                      : `Next charge: ${formatDateOrFallback(nextBillingDate)}`}
                  </Typography>
                </Grid>
                {anchorDescription ? (
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      {anchorDescription}
                    </Typography>
                  </Grid>
                ) : null}
                {lastSuccessfulCycleAt ? (
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      Last successful charge:{" "}
                      {formatDateOrFallback(lastSuccessfulCycleAt)}
                    </Typography>
                  </Grid>
                ) : null}
                {lastError?.message ? (
                  <Grid item container spacing={1} alignItems="center">
                    <Grid item>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" color="error">
                        {lastError.message}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : null}
                {actionError ? (
                  <Grid item>
                    <Typography variant="body2" color="error">
                      {actionError}
                    </Typography>
                  </Grid>
                ) : null}
                {relatedOffsets.length ? (
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      {relatedOffsets.length} offset purchase
                      {relatedOffsets.length === 1 ? "" : "s"} (
                      {formatDecimal(
                        relatedOffsets.reduce(
                          (acc, { tonsCo2e }) => acc + (tonsCo2e || 0),
                          0
                        )
                      )}{" "}
                      tons total)
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Grid container spacing={1} justifyContent="flex-end">
                {isActive ? (
                  <Grid item>
                    <Tooltip title="Pause">
                      <IconButton
                        onClick={pauseSubscription}
                        disabled={actionLoading}
                      >
                        <FontAwesomeIcon icon={faPause} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ) : null}
                {isPaused ? (
                  <Grid item>
                    <Button
                      onClick={resumeSubscription}
                      disabled={actionLoading}
                      variant="contained"
                      color="secondary"
                      size="small"
                      startIcon={<FontAwesomeIcon icon={faPlay} />}
                    >
                      Resume
                    </Button>
                  </Grid>
                ) : null}
                {!isCanceled ? (
                  <Grid item>
                    <Tooltip title="Update payment method">
                      <IconButton
                        onClick={() => setIsUpdatingPayment(true)}
                        disabled={actionLoading}
                      >
                        <FontAwesomeIcon icon={faCreditCard} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ) : null}
                {!isCanceled ? (
                  <Grid item>
                    <Tooltip title="Cancel subscription">
                      <IconButton
                        onClick={cancelSubscription}
                        disabled={actionLoading}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      }
    />
    </>
  );
};

const SubscriptionsManagementTab = () => {
  const [subscriptions, subscriptionsLoading] = useCachedDisplayData(
    "offset-subscriptions"
  );
  const [offsets, offsetsLoading] = useCachedDisplayData("offsets");

  if (subscriptionsLoading || offsetsLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={4}>
        <AnimatedLogo />
      </Box>
    );
  }

  const activeAndPaused = (subscriptions || []).filter(
    ({ status }) => status !== "canceled"
  );
  const canceled = (subscriptions || []).filter(
    ({ status }) => status === "canceled"
  );

  if (!subscriptions?.length) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" color="textPrimary">
          You don't have any offset subscriptions yet
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Choose a portfolio from the Browse Portfolios tab and select
          "Subscribe" to set up recurring offset purchases.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        {activeAndPaused.map((subscription) => (
          <SubscriptionCard
            key={subscription.id}
            subscription={subscription}
            offsets={offsets}
          />
        ))}
        {canceled.length ? (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Canceled subscriptions
            </Typography>
          </Grid>
        ) : null}
        {canceled.map((subscription) => (
          <SubscriptionCard
            key={subscription.id}
            subscription={subscription}
            offsets={offsets}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default SubscriptionsManagementTab;
